import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { ArrowLeft, CheckCircle2, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = { cabinet_name: '', first_name: '', last_name: '', contact_email: '',
  telephone: '', city: '', country: 'France', structure: '', password: '', message: '' };

export default function RegisterPage() {
  const [form, setForm] = useState(EMPTY);
  const [structures, setStructures] = useState([]);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    axios.get(`${API}/register/structures`).then((r) => setStructures(r.data)).catch(() => {});
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.structure) { toast.error('Choisissez votre type de structure'); return; }
    if (form.password.length < 8) { toast.error('Le mot de passe doit contenir au moins 8 caractères'); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/register/cabinet`, form);
      setDone(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de l'envoi de la demande");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4 sm:p-8" data-testid="register-page">
      <div className="w-full max-w-2xl">
        <Link to="/login" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-4" data-testid="register-back-login">
          <ArrowLeft className="h-4 w-4" />Retour à la connexion
        </Link>

        {done ? (
          <Card className="border-border">
            <CardContent className="py-12 text-center space-y-3">
              <CheckCircle2 className="h-12 w-12 text-emerald-600 mx-auto" />
              <h2 className="text-xl font-bold" data-testid="register-success">Demande envoyée !</h2>
              <p className="text-sm text-muted-foreground max-w-md mx-auto">
                Votre demande d'inscription a bien été reçue. Elle sera examinée par notre équipe
                sous 48 h ouvrées. Vous recevrez un email de confirmation dès validation.
              </p>
              <Link to="/login"><Button variant="outline" className="mt-2">Retour à la connexion</Button></Link>
            </CardContent>
          </Card>
        ) : (
          <Card className="border-border shadow-sm">
            <CardHeader>
              <CardTitle className="text-xl sm:text-2xl font-bold">Inscription cabinet dentaire</CardTitle>
              <CardDescription>
                Rejoignez Jeff Connect. Votre demande sera validée par notre équipe avant l'activation du compte.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={submit} className="space-y-4">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-2 sm:col-span-2">
                    <Label>Nom du cabinet *</Label>
                    <Input value={form.cabinet_name} onChange={set('cabinet_name')} required minLength={2} placeholder="Cabinet Dentaire du Parc" data-testid="register-cabinet-name" />
                  </div>
                  <div className="space-y-2">
                    <Label>Prénom *</Label>
                    <Input value={form.first_name} onChange={set('first_name')} required data-testid="register-first-name" />
                  </div>
                  <div className="space-y-2">
                    <Label>Nom *</Label>
                    <Input value={form.last_name} onChange={set('last_name')} required data-testid="register-last-name" />
                  </div>
                  <div className="space-y-2">
                    <Label>Email *</Label>
                    <Input type="email" value={form.contact_email} onChange={set('contact_email')} required data-testid="register-email" />
                  </div>
                  <div className="space-y-2">
                    <Label>Téléphone</Label>
                    <Input value={form.telephone} onChange={set('telephone')} data-testid="register-phone" />
                  </div>
                  <div className="space-y-2">
                    <Label>Ville</Label>
                    <Input value={form.city} onChange={set('city')} data-testid="register-city" />
                  </div>
                  <div className="space-y-2">
                    <Label>Pays</Label>
                    <Input value={form.country} onChange={set('country')} data-testid="register-country" />
                  </div>
                  <div className="space-y-2">
                    <Label>Type de structure *</Label>
                    <Select value={form.structure} onValueChange={(v) => setForm({ ...form, structure: v })}>
                      <SelectTrigger data-testid="register-structure-select"><SelectValue placeholder="Choisir..." /></SelectTrigger>
                      <SelectContent>
                        {structures.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Mot de passe * (8 caractères min.)</Label>
                    <Input type="password" value={form.password} onChange={set('password')} required minLength={8} data-testid="register-password" />
                  </div>
                  <div className="space-y-2 sm:col-span-2">
                    <Label>Message (facultatif)</Label>
                    <Textarea value={form.message} onChange={set('message')} rows={3}
                      placeholder="Présentez brièvement votre cabinet, vos besoins..." data-testid="register-message" />
                  </div>
                </div>
                <Button type="submit" className="w-full" disabled={loading} data-testid="register-submit-btn">
                  {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Envoyer ma demande d'inscription
                </Button>
              </form>
            </CardContent>
          </Card>
        )}
        <div className="mt-4 flex flex-wrap justify-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
          <Link to="/aide" className="hover:underline" data-testid="register-link-aide">Aide</Link>
          <Link to="/mentions-legales" className="hover:underline" data-testid="register-link-mentions">Mentions légales</Link>
          <Link to="/cgu" className="hover:underline" data-testid="register-link-cgu">CGU</Link>
          <Link to="/confidentialite" className="hover:underline" data-testid="register-link-confidentialite">Confidentialité</Link>
        </div>
      </div>
    </div>
  );
}
